/**
 * @file OrderPaymentsTab.tsx
 * @description Вкладка «Оплаты» заказа (phase10). Её перестраивает phase20,
 * в phase19.31 — минимум по SC50: кнопки темы, «₽» вместо кода валюты,
 * номер платежа обычным шрифтом, тона статусов (SC26). Причина возврата
 * платежа — в окне кабинета `ReasonDialog` (было `window.prompt`).
 */

import { CreditCard } from "lucide-react";
import { useEffect, useState } from "react";

import { Alert } from "../ui/alert";
import { Button } from "../ui/button";
import { EditorialCard } from "../ui/EditorialCard";
import { useNotify } from "../ui/NotifyProvider";
import { OrderPaymentCard } from "./OrderPaymentCard";
import { OrderPaymentCreateDialog } from "./OrderPaymentCreateDialog";
import { ReasonDialog } from "./ReasonDialog";
import { useFocusedRow } from "../../hooks/useFocusedRow";
import { useOrderTabRevisit } from "../../hooks/useOrderTabRevisit";
import { ApiError } from "../../lib/api-client";
import { OrderTabSkeleton } from "./OrderTabSkeleton";
import {
  currencySymbol,
  formatMoney,
  listPayments,
  refundPayment,
  type OrderDto,
  type PaymentDto,
} from "../../lib/api-orders";

interface Props {
  order: OrderDto;
  canManage: boolean;
  onChanged?: () => void;
  /** id платежа из `?focus=` — узел «Платёж» «Пути заказа» ведёт в эту строку (§4). */
  focusId?: string | null;
  /** Вкладка сейчас открыта: при возврате на неё список дочитывается тихо. */
  active?: boolean;
}

export function OrderPaymentsTab({ order, canManage, onChanged, focusId, active = true }: Props) {
  const notify = useNotify();
  const focused = useFocusedRow(focusId);
  const rub = currencySymbol(order.currency);
  const [items, setItems] = useState<PaymentDto[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  // Платёж, по которому открыто окно возврата.
  const [refunding, setRefunding] = useState<PaymentDto | null>(null);
  const [refundBusy, setRefundBusy] = useState(false);

  async function refresh() {
    try {
      setItems(await listPayments(order.id));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Не удалось загрузить платежи");
    }
  }

  useEffect(() => {
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order.id]);
  useOrderTabRevisit(active, refresh);

  /** Возврат платежа с причиной из окна; `true` — окно можно закрыть. */
  async function onRefund(reason: string): Promise<boolean> {
    if (!refunding) return false;
    setRefundBusy(true);
    try {
      await refundPayment(refunding.id, { reason });
      notify.success("Возврат оформлен");
      void refresh();
      onChanged?.();
      return true;
    } catch (err) {
      notify.error(err instanceof ApiError ? err.message : "Не удалось оформить возврат");
      return false;
    } finally {
      setRefundBusy(false);
    }
  }

  if (error) return <Alert severity="error">{error}</Alert>;
  if (items === null) {
    return (
      // Заготовка высотой блока, а не спиннер: страница не прыгает (qa4 п. 14).
      <OrderTabSkeleton />
    );
  }

  const totalPaid = items
    .filter((p) => p.status === "COMPLETED")
    .reduce((sum, p) => sum + Number(p.amount), 0);
  const balance = Number(order.totalGross) - totalPaid;

  return (
    <div className="flex flex-col gap-3">
      <EditorialCard>
        <EditorialCard.Body>
          {/* Итоги — данными: подпись, сумма и тон остатка. */}
          <dl className="type-body grid grid-cols-1 gap-3 sm:grid-cols-3">
            {[
              { label: "К оплате", value: order.totalGross, tone: "" },
              { label: "Оплачено", value: String(totalPaid), tone: "" },
              {
                label: "Остаток",
                value: String(balance),
                tone: balance > 0 ? "text-[var(--status-attention-fg,var(--warning))]" : "text-[var(--status-success-fg,var(--success))]",
              },
            ].map((cell) => (
              <div key={cell.label}>
                <dt className="type-caption text-on-surface-variant">{cell.label}</dt>
                <dd className={`font-semibold tabular-nums ${cell.tone}`}>
                  {formatMoney(cell.value)} {rub}
                </dd>
              </div>
            ))}
          </dl>
        </EditorialCard.Body>
      </EditorialCard>

      {canManage ? (
        <div className="flex justify-end">
          <Button variant="outline" onClick={() => setCreating(true)}>
            <CreditCard className="size-4" />
            Зафиксировать платёж
          </Button>
        </div>
      ) : null}

      {items.length === 0 ? (
        <EditorialCard>
          <EditorialCard.Body>
            <p className="type-body py-8 text-center text-on-surface-variant">Платежей пока нет</p>
          </EditorialCard.Body>
        </EditorialCard>
      ) : (
        items.map((p) => (
          <OrderPaymentCard
            key={p.id}
            payment={p}
            rowAttr={focused.rowAttr(p.id)}
            highlighted={focused.highlightedId === p.id}
            onRefund={canManage && p.status === "COMPLETED" ? () => setRefunding(p) : undefined}
          />
        ))
      )}

      <ReasonDialog
        open={refunding !== null}
        busy={refundBusy}
        danger
        onClose={() => setRefunding(null)}
        onConfirm={onRefund}
        texts={{
          title: refunding?.number ? `Вернуть платёж ${refunding.number}?` : "Вернуть платёж?",
          description: refunding
            ? `Платёж на ${formatMoney(refunding.amount)} ${currencySymbol(refunding.currency)} будет отмечен как возвращённый. Причина сохранится в платеже.`
            : "",
          label: "Причина возврата",
          placeholder: "Клиент отказался от заказа, оплата прошла дважды…",
          confirm: "Вернуть платёж",
          cancel: "Не возвращать",
        }}
      />

      {creating ? (
        <OrderPaymentCreateDialog
          order={order}
          balance={balance}
          onClose={() => setCreating(false)}
          onCreated={() => {
            setCreating(false);
            void refresh();
            onChanged?.();
          }}
        />
      ) : null}
    </div>
  );
}

